import {l} from "types/typescriptdefinisjoner_lederplan";
import {gettextCatalog} from "angular-gettext";
import moment from "moment";
import {regNavn} from "./tredjepartsTjenestenavn";
import _ from "lodash";
import {formaterByteSomLesbarStreng} from "../stoettefunk/byteStørrelseFormatering";
import {DokumentsigneringTjeneste} from "./modulspesifikke/DokumentsigneringTjeneste";

export class StrengMaler {

    static regNavn = "StrengMaler";

    /** regexp som svarer sann dersom malnavn representerer en migrert sp2 endringshendelse */
    private static sp2MigreringMalnavn = new RegExp(`-${l.SubEndringstype.sp2Endringshendelse}|-${l.SubEndringstype.sp2SignEndringshendelse}|-${l.SubEndringstype.sp2SmsEndringshendelse}`)

    static forInjisering() {
        return [
            DokumentsigneringTjeneste.regNavn,
            regNavn.gettext.gettextCatalog,
            StrengMaler
        ]
    }

    constructor(private dokumentsigneringTjeneste: DokumentsigneringTjeneste, private gettextCatalog: gettextCatalog) {
    }

    //offentlige metoder

    somKortStreng(mal: l.StrengMal<any>): string {
        //spesialhåndtering av migrerte sp2 endringshendelser - her ligger ferdig html-formatert melding i beskrivelse-strukturen
        if (StrengMaler.sp2MigreringMalnavn.test(mal.malnavn)) {
            return (<Sp2MigrertBeskrivelsesArg>mal.noeklerOgVerdier.beskrivelse).message;
        }

        // anonymiserte hendelser. foreløpig kun tilgjegelig for admin - altså oss.
        // hvis disse skal vises til brukere, så må det gjøres endringer.
        if (mal.malnavn && mal.malnavn.endsWith('-anonymisert')) {
            return 'Anonymisert hendelse';
        }

        if (!this.maler[mal.malnavn]) {
            console.warn("StrengMaler.somKortStreng(..), ugyldig mal-spesifikasjon anitt", mal);
            return mal.malnavn ? mal.malnavn : "";
        }

        try {
            return this.maler[mal.malnavn].kort(mal);
        } catch (e) {
            console.warn("StrengMaler.somKortStreng(..), feil under bygging av streng", mal, e);
            return mal.malnavn ? mal.malnavn : "";
        }
    }

    somFullStreng(mal: l.StrengMal<any>): string {
        if (!this.maler[mal.malnavn]) {
            console.warn("StrengMaler.somFullStreng(..), ugyldig mal-spesifikasjon anitt", mal);
            return mal.malnavn ? mal.malnavn : "";
        }

        try {
            return this.maler[mal.malnavn].full(mal);
        } catch (e) {
            console.warn("StrengMaler.somFullStreng(..), feil under bygging av streng", mal, e);
            return mal.malnavn ? mal.malnavn : "";
        }
    }

    //hjelpemetoder

    /** Converts the characters "&", "<", ">", '"', and "'" in string to their corresponding HTML entities. (dok fra _.escape) */
    private somSikker(tekst: string) {
        return _.escape(tekst);
    }

    private fmtDato(dato: Date) {
        return moment(dato).format("L")
    }

    private endringshendelseHovedlinje(tekst: string) {
        return `<span class='endringshendelse-hoved'>${tekst}</span>`;
    }

    private endringshendelseSublinje(tekst: string) {
        return `<span class='endringshendelse-sub'>${tekst}</span>`;
    }


    private tilGruppe(mal: l.StrengMalEndringshendelse<Gruppe>) {
        return this.gettextCatalog.getString('til {{gruppe}}', {gruppe: this.somSikker(mal.noeklerOgVerdier.beskrivelse.gruppe)});
    }

    private iGruppe(mal: l.StrengMalEndringshendelse<Gruppe>) {
        return this.gettextCatalog.getString('i {{gruppe}}', {gruppe: this.somSikker(mal.noeklerOgVerdier.beskrivelse.gruppe)});
    }

    private iGruppeOgOrg(mal: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) {
        const b = mal.noeklerOgVerdier.beskrivelse;
        return this.gettextCatalog.getString('i {{gruppenavn}} i {{organisasjonsnavn}}',
          {gruppenavn: this.somSikker(b.gruppenavn), organisasjonsnavn: this.somSikker(b.organisasjonsnavn)});
    }

    private iMøte(mal: l.StrengMalEndringshendelse<Møte>) {
        const b = mal.noeklerOgVerdier.beskrivelse;
        return this.gettextCatalog.getString('i {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}',
          {moeteTittel: this.somSikker(b.møteTittel), moeteNummer: this.somSikker(b.møteNummer), moeteDato: this.formaterMøteDato(b.møteDato)});
    }

    private forMøte(mal: l.StrengMalEndringshendelse<Møte>) {
        const b = mal.noeklerOgVerdier.beskrivelse;
        return this.gettextCatalog.getString('for {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}',
          {moeteTittel: this.somSikker(b.møteTittel), moeteNummer: this.somSikker(b.møteNummer), moeteDato: this.formaterMøteDato(b.møteDato)});
    }

    private iSakOgMøte(mal: l.StrengMalEndringshendelse<MøteOgSak>) {
        const b = mal.noeklerOgVerdier.beskrivelse;
        return this.gettextCatalog.getString('i sak {{sakNummer}} {{sakTittel}} i {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}',
          {
              sakNummer: this.somSikker(b.sakNummer),
              sakTittel: this.somSikker(b.sakTittel),
              moeteTittel: this.somSikker(b.møteTittel),
              moeteNummer: this.somSikker(b.møteNummer),
              moeteDato: this.formaterMøteDato(b.møteDato)
          });
    }

    private formaterMøteDato(møteDato: l.MøteDato) {
        try {
            return moment(møteDato.start).format("L");
        } catch (e) {
            console.warn("StrengMaler.formaterMøteDato(..), feil under formatering av dato", møteDato, e);
            return '';
        }
    }

    private formaterDato(dato) {
        try {
            return moment(dato).format("L");
        } catch (e) {
            console.warn("StrengMaler.formaterDato(..), feil under formatering av dato", dato, e);
            return '';
        }
    }

    private iTag(verdi) {
        return `<i>${this.somSikker(verdi)}</i>`;
    }

    //maltekstene

    /** malene våre */
    private maler: { [nøkkel: string]: { kort: (mal: l.StrengMal<any>) => string; full: (mal: l.StrengMal<any>) => string } } = {

        /** se StrengMal#møteBeskrivelse i lederplan-tjenester  */
        møte: {
            kort: (m: l.StrengMal<MøteBeskrivelse>) => `${this.gettextCatalog.getString('møte')} <span class='mal-moete'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span>`,
            full: (m: l.StrengMal<MøteBeskrivelse>) => `<span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`
        },

        /** se StrengMal#sakBeskrivelse i lederplan-tjenester  */
        sak: {
            kort: (m: l.StrengMal<SaksBeskrivelse>) => `${this.gettextCatalog.getString('sak i')} <span class='mal-moete'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span>`,
            full: (m: l.StrengMal<SaksBeskrivelse>) => `<span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                        <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`
        },

        /** se StrengMal#opplastetDokumentversjonBeskrivelse i lederplan-tjenester  */
        opplastetdokumentversjon: {
            kort: (m: l.StrengMal<DokumentAnnoteringKommentarBeskrivelse>) => `${this.gettextCatalog.getString('dokument')} <span class='mal-dokument'>${this.somSikker(m.noeklerOgVerdier.dokument)}</span>`,
            full: (m: l.StrengMal<DokumentAnnoteringKommentarBeskrivelse>) => `<span class="stielement"><i class="entitet_ikon entitet_ikon_dokument"></i> <span class='mal-oppg stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.dokument)}</span></span>` +
              (m.noeklerOgVerdier.mappenavn ? `<span class="stielement"><i class="entitet_ikon entitet_ikon_arkivmappenode"></i> <span class="mal-mappe stielement-tekst">${this.somSikker(m.noeklerOgVerdier.mappenavn)}</span></span>` :
                `<span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                        <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`)
        },

        /** se StrengMal#beslutningsBeskrivelse i lederplan-tjenester */
        beslutning: {
            kort: (m: l.StrengMal<BeslutningBeskrivelse>) => `${this.gettextCatalog.getString('beslutning')} <span class='mal-beslutning'>${this.somSikker(m.noeklerOgVerdier.beslutning)}</span>`,
            full: (m: l.StrengMal<BeslutningBeskrivelse>) => `<span class="stielement"><i class="entitet_ikon entitet_ikon_beslutning"></i> <span class='mal-oppg stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.beslutning)}</span></span>
                        <span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                        <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`
        },

        /** se StrengMal#oppgaveBeskrivelse i lederplan-tjenester
         * merk at oppgave er knyttet til en av: oppgave under sak, oppgave under beslutning under under sak, frittstående oppgave */
        oppgave: {
            kort: (m: l.StrengMal<OppgaveBeskrivelse>) => `${this.gettextCatalog.getString('oppgave')} <span class='mal-oppgave'>${this.somSikker(m.noeklerOgVerdier.oppgave)}</span>`,
            full: (m: l.StrengMal<OppgaveBeskrivelse>) => `<span class="stielement"><i class="entitet_ikon entitet_ikon_oppgave"></i> <span class='mal-oppg stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.oppgave)}</span></span>` +
              (m.noeklerOgVerdier.beslutning ? `<span class="stielement"><i class="entitet_ikon entitet_ikon_beslutning"></i> <span class='mal-besl stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.beslutning)}</span></span>` : '') +
              (m.noeklerOgVerdier.sak ? `<span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                        <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>` : '')
        },

        /** se StrengMal#saksKommentarBeskrivelse i lederplan-tjenester  */
        sakskommentar: {
            kort: (m: l.StrengMal<SaksKommentarBeskrivelse>) => `${this.gettextCatalog.getString('sak i')} <span class='mal-moete'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span>`,
            full: (m: l.StrengMal<SaksKommentarBeskrivelse>) => `
                <span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`
        },

        /** se StrengMal#oppgaveKommentarBeskrivelse i lederplan-tjenester
         * merk at oppgavekommentar er knyttet til en av: oppgave under sak, oppgave under beslutning under under sak, frittstående oppgave */
        oppgavekommentar: {
            kort: (m: l.StrengMal<OppgaveKommentarBeskrivelse>) => `${this.gettextCatalog.getString('oppgave')} <span class='mal-oppgave'>${this.somSikker(m.noeklerOgVerdier.oppgave)}</span>`,
            full: (m: l.StrengMal<OppgaveKommentarBeskrivelse>) => `
                <span class="stielement"><i class="entitet_ikon entitet_ikon_oppgave"></i> <span class='mal-oppgave stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.oppgave)}</span></span>` +
              (m.noeklerOgVerdier.beslutning ?
                `<span class="stielement"><i class="entitet_ikon entitet_ikon_beslutning"></i> <span class='mal-beslutning stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.beslutning)}</span></span>` : '') +
              (m.noeklerOgVerdier.sak ?
                `<span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                 <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>` : '')
        },

        /** se StrengMal#dokumentAnnoteringKommentarBeskrivelse i lederplan-tjenester  */
        dokumentannoteringkommentar: {
            kort: (m: l.StrengMal<DokumentAnnoteringKommentarBeskrivelse>) => `${this.gettextCatalog.getString('dokument')} <span class='mal-dokument'>${this.somSikker(m.noeklerOgVerdier.dokument)}</span>`,
            full: (m: l.StrengMal<DokumentAnnoteringKommentarBeskrivelse>) => `
                <span class="stielement"><i class="entitet_ikon entitet_ikon_dokument"></i> <span class='mal-dokument stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.dokument)}</span></span>` +
              (m.noeklerOgVerdier.mappenavn ?
                `<span class="stielement"><i class="entitet_ikon entitet_ikon_arkivmappenode"></i> <span class="mal-mappe stielement-tekst">${this.somSikker(m.noeklerOgVerdier.mappenavn)}</span></span>` :
                `<span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                 <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`)
        },

        /** se StrengMal#saksOppgaveBeskrivelse i lederplan-tjenester */
        saksoppgave: {
            kort: (m: l.StrengMal<SaksOppgaveBeskrivelse>) => `${this.gettextCatalog.getString('sak i')} <span class='mal-moete'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span>`,
            full: (m: l.StrengMal<SaksOppgaveBeskrivelse>) => `
                <span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`
        },

        /** se StrengMal#beslutningsOppgaveBeskrivelse i lederplan-tjenester */
        beslutningsoppgave: {
            kort: (m: l.StrengMal<BeslutningsOppgaveBeskrivelse>) => `${this.gettextCatalog.getString('beslutning i')} <span class='mal-moete'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span>`,
            full: (m: l.StrengMal<BeslutningsOppgaveBeskrivelse>) => `
                <span class="stielement"><i class="entitet_ikon entitet_ikon_beslutning"></i> <span class='mal-beslutning stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.beslutning)}</span></span>
                <span class="stielement"><i class="entitet_ikon entitet_ikon_sak"></i> <span class='mal-sak stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.sak)}</span></span>
                <span class="stielement"><i class="entitet_ikon entitet_ikon_moete"></i> <span class='mal-moete stielement-tekst'>${this.somSikker(m.noeklerOgVerdier.moete)} ${this.fmtDato(m.noeklerOgVerdier.moetedato)}</span></span>`,
        },

        /*
        Endringshendelser, tekstnøkkel er på formen "endringshendelser-<entitet_klassenavn>-<Endringstype.type>[-<SubEndringstype.type>]

        Mulige Endringstype er opprett(0), rediger(1), slett(2), innlogging(3), innlogging_feilet(4), utlogging(5), epost(6)
        Mulige SubEndringstype er flyttTilMøte(0), referatOppdatert(1), dokumentversjon(2), protokollSignert(3), protokollKlarTilSignering(4), protokollLåstOpp(5), epostadresseBekreftet(6), avstemningslaternativerEndret(7), avstemningsresultatEndret(8), avstemningsresultatNullstilt(9), avstemningAapnet(10), avstemningStengt(11)

        Se no.systemfabrikken.lederplan.domene.revisjonslogg.BeskrivelsesargumenterFabrikk for definisjon av beskrivelsesargumenter
        */

        /** Se Avtale i lederplan-tjenester  */
        'endringshendelse-Avtale-0': {
            kort: (m: l.StrengMalEndringshendelse<AvtaleBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Avtale {{navn}} opprettet', {
                  navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)
              })),
            full: (m: l.StrengMalEndringshendelse<AvtaleBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Avtale#eksporterTilZip i lederplan-tjenester  */
        'endringshendelse-Avtale-7': {
            kort: (m: l.StrengMalEndringshendelse<AvtaleBeskrivelsesArg & { stoerrelse: number }>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Data eksportert for avtale {{navn}} {{stoerrelse}}', {
                  navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn),
                  stoerrelse: formaterByteSomLesbarStreng(m.noeklerOgVerdier.beskrivelse.stoerrelse),
              })),
            full: (m: l.StrengMalEndringshendelse<AvtaleBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møte#nyMøteEndringshendelse i lederplan-tjenester  */
        'endringshendelse-Møte-0': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Nytt {{moeteTittel}} {{moeteNummer}}, {{moeteDato}} registrert', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se KopierMøteFabrikk#opprettMøte i lederplan-tjenester  */
        'endringshendelse-Møte-0-20': {
            kort: (m: l.StrengMalEndringshendelse<MøteKopiBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Nytt {{moeteTittel}} {{moeteNummer}}, {{moeteDato}} registrert som kopi av {{kildemoeteTittel}} {{kildemoeteNummer}}, {{kildemoeteDato}} i {{kildemoeteGruppenavn}} i {{kildemoeteOrganisasjonsnavn}}', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato),
                    kildemoeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.kildemøte_tittel),
                    kildemoeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.kildemøte_nummer),
                    kildemoeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.kildemøte_dato),
                    kildemoeteGruppenavn: this.somSikker(m.noeklerOgVerdier.beskrivelse.kildemøte_gruppenavn),
                    kildemoeteOrganisasjonsnavn: this.somSikker(m.noeklerOgVerdier.beskrivelse.kildemøte_organisasjonsnavn)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møte#nyMøteEndringshendelse i lederplan-tjenester */
        'endringshendelse-Møte-1': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{moeteTittel}} {{moeteNummer}}, {{moeteDato}} oppdatert', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møteprotokoll#sendTilSignering i lederplan-tjenester */
        'endringshendelse-Møte-1-4': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Protokoll fra {{moeteTittel}} {{moeteNummer}}, {{moeteDato}} er klar til signering', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møteprotokoll#låsOppProtokoll i lederplan-tjenester */
        'endringshendelse-Møte-1-5': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Protokoll fra {{moeteTittel}} {{moeteNummer}}, {{moeteDato}} er låst opp', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møte.Avstemning#åpneForAvstemning i lederplan-tjenester  */
        'endringshendelse-Møte-1-9': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Åpnet for avstemning på alle beslutninger i {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møte.Avstemning#stengForAvstemning i lederplan-tjenester  */
        'endringshendelse-Møte-1-10': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Stengt for avstemning på alle beslutninger i {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se MøteSkjerming#endringshendelsebygger i lederplan-tjenester  */
        'endringshendelse-Møte-1-16': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Tilgangsbegrensninger for møte {{moeteTittel}} {{moeteNummer}}, {{moeteDato}} oppdatert', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se MøteSkjerming#endringshendelsebygger i lederplan-tjenester  */
        'endringshendelse-Møte-1-17': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Publisering endret for møte {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** se no.systemfabrikken.lederplan.domene.møte.Møteprotokoll#oppdaterProtokollførteMøtedetaljer */
        'endringshendelse-Møte-1-28': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Protokoll oppdatert for {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se MøteRessurs#slett i lederplan-tjenester */
        'endringshendelse-Møte-2': {
            kort: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{moeteTittel}} {{moeteNummer}}, {{moeteDato}} slettet', {
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.tittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.dato)
                })) +
              this.endringshendelseSublinje(this.iGruppeOgOrg(m)),
            full: (m: l.StrengMalEndringshendelse<MøteBeskrivelsesArg>) => "" //bruk kort
        },

        ///** Se migrert fra sp2 (rediger/sp2Endringshendelse) */
        //'endringshendelse-Møte-1-22': {
        //    kort: (m: l.StrengMalEndringshendelse<Sp2MigrertBeskrivelsesArg>) => m.noeklerOgVerdier.beskrivelse.message,
        //    full: (m: l.StrengMalEndringshendelse<Sp2MigrertBeskrivelsesArg>) => "" //bruk kort
        //},

        /** Se Møtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-Møtedeltaker-0': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{navn}} lagt til som møtedeltaker', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-Møtedeltaker-1': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Møtedeltaker {{navn}} oppdatert', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },


        /** Se Møtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-Møtedeltaker-2': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{navn}} fjernet som møtedeltaker', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Møtedeltaker.Stemmegivning#registrerStemme i lederplan-tjenester */
        'endringshendelse-Møtedeltaker-1-7': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakersStemmegivningBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Møtedeltaker {{navn}} har avgitt stemme på avstemningen "{{avstemningTittel}}"', {
                    navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn),
                    avstemningTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.avstemningTittel)
                })) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakersStemmegivningBeskrivelsesArg>) => "" //bruk kort "beskrivelse":{"noeklerOgVerdier":{"feltendringer":{"stemming":{"gammelVerdi":null,"nyVerdi":"Ja"}},
        },

        /** Se Møtedeltaker.Stemmegivning#nullstill i lederplan-tjenester.
         * Opprinnelig stemmegivning ligger i m.noeklerOgVerdier.feltendringer.stemming.
         * Dersom stemmegivning er nullstilt på kun en enkelt beslutning vil feltet avstemningTittel være tilgjengelig */
        'endringshendelse-Møtedeltaker-1-8': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakersStemmegivningBeskrivelsesArg>) => {
                if (!_.isNil(m.noeklerOgVerdier.beskrivelse.sakNummer))
                    return this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                        'Avgitte stemmer nullstilt for møtedeltaker {{navn}} på avstemningen "{{avstemningTittel}}"', {
                            navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn),
                            avstemningTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.avstemningTittel)
                        })) +
                      this.endringshendelseSublinje(this.iMøte(m))
                else
                    return this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                        'Avgitte stemmer nullstilt for møtedeltaker {{navn}} på alle avstemninger', {
                            navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)
                        })) +
                      this.endringshendelseSublinje(this.iMøte(m))
            },
            full: (m: l.StrengMalEndringshendelse<MøtedeltakersStemmegivningBeskrivelsesArg>) => "" //bruk kort "beskrivelse":{"noeklerOgVerdier":{"feltendringer":{"stemming":{"gammelVerdi":null,"nyVerdi":"Ja"}},
        },

        /** Se EksternMøtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-EksternMøtedeltaker-0': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{navn}} lagt til som ekstern møtedeltaker', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se EksternMøtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-EksternMøtedeltaker-1': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Ekstern møtedeltaker {{navn}} oppdatert', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },


        /** Se MøtedeEksternMøtedeltakerltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-EksternMøtedeltaker-2': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{navn}} fjernet som ekstern møtedeltaker', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se EksternProtokollførtMøtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-EksternProtokollførtMøtedeltaker-0': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{navn}} lagt til som ekstern protokollført møtedeltaker', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se EksternProtokollførtMøtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-EksternProtokollførtMøtedeltaker-1': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Ekstern protokollført møtedeltaker {{navn}} oppdatert', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },


        /** Se EksternProtokollførtMøtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-EksternProtokollførtMøtedeltaker-2': {
            kort: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                '{{navn}} fjernet som ekstern protokollført møtedeltaker', {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<MøtedeltakerBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se ProtokollførtMøtedeltaker#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-ProtokollførtMøtedeltaker-0': {
            kort: (m: l.StrengMalEndringshendelse<ProtokollførtMøtedeltakerBeskrivelsesArg>) => {
                const verdier = {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)};
                switch (m.noeklerOgVerdier.beskrivelse.status) {
                    case l.ProtokollførtDeltakerstatus.deltatt:
                        return this.endringshendelseHovedlinje(this.gettextCatalog.getString('{{navn}} registrert som deltatt i protokollen', verdier)) +
                          this.endringshendelseSublinje(this.forMøte(m));
                    case l.ProtokollførtDeltakerstatus.forfall:
                        return this.endringshendelseHovedlinje(this.gettextCatalog.getString('{{navn}} registrert med forfall i protokollen', verdier)) +
                          this.endringshendelseSublinje(this.forMøte(m));
                    case l.ProtokollførtDeltakerstatus.ikkeInnkalt:
                        return this.endringshendelseHovedlinje(this.gettextCatalog.getString('{{navn}} registrert som ikke innkalt i protokollen', verdier)) +
                          this.endringshendelseSublinje(this.forMøte(m));
                }
            },
            full: m => "" //bruk kort
        },

        'endringshendelse-ProtokollførtMøtedeltaker-1': {
            kort: (m: l.StrengMalEndringshendelse<ProtokollførtMøtedeltakerBeskrivelsesArg>) => {
                const verdier = {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)};
                return this.endringshendelseHovedlinje(this.gettextCatalog.getString('{{navn}} oppdatert i protokollen', verdier)) +
                  this.endringshendelseSublinje(this.forMøte(m));
            },
            full: m => "" //bruk kort
        },

        'endringshendelse-ProtokollførtMøtedeltaker-2': {
            kort: (m: l.StrengMalEndringshendelse<ProtokollførtMøtedeltakerBeskrivelsesArg>) => {
                const verdier = {navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn)};
                return this.endringshendelseHovedlinje(this.gettextCatalog.getString('{{navn}} slettet fra protokollen', verdier)) +
                  this.endringshendelseSublinje(this.forMøte(m));
            },
            full: m => "" //bruk kort
        },

        'endringshendelse-Signatar-1-3': {
            kort: (m: l.StrengMalEndringshendelse<SignertHendelseArg>) => {
                const hovedlinje = this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                  '{{navn}} signerte {{filnavn}} med {{signeringsmetode}}', {
                      navn: this.somSikker(m.noeklerOgVerdier.beskrivelse.navn),
                      filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn),
                      signeringsmetode: this.dokumentsigneringTjeneste.signaturtypeI18nNavn(m.noeklerOgVerdier.beskrivelse.signeringsmetode)
                  }));

                if (m.noeklerOgVerdier.beskrivelse.mappenavn) {
                    //arkivdokument
                    return hovedlinje + this.endringshendelseSublinje(this.gettextCatalog.getString(
                      'i arkivmappe {{mappenavn}} ', {mappenavn: this.iTag(m.noeklerOgVerdier.beskrivelse.mappenavn)}));
                } else if (m.noeklerOgVerdier.beskrivelse.sakNummer) {
                    //saksdokument
                    return hovedlinje + this.endringshendelseSublinje(this.iSakOgMøte(m));
                } else {
                    //protokolldokument
                    return hovedlinje + this.endringshendelseSublinje(this.iMøte(m));
                }
            },
            full: m => "" //bruk kort
        },

        /** Se Sak#nySakEndringshendelse i lederplan-tjenester */
        'endringshendelse-Sak-0': {
            kort: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Ny sak {{sakNummer}} {{sakTittel}} registrert', {
                    sakTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                    sakNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer)
                })) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Sak#nySakEndringshendelse i lederplan-tjenester */
        'endringshendelse-Sak-1': {
            kort: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Sak {{sakNummer}} {{sakTittel}} oppdatert', {
                    sakTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                    sakNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer)
                })) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Sak#flyttTilMøte i lederplan-tjenester */
        'endringshendelse-Sak-1-0': {
            kort: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Sak {{sakNummer}} {{sakTittel}} er flyttet til {{moeteTittel}} {{moeteNummer}}, {{moeteDato}}', {
                    sakTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                    sakNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.møteTittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.møteNummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.møteDato)
                })) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Sak#oppdaterReferat i lederplan-tjenester */
        'endringshendelse-Sak-1-1': {
            kort: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Protokoll fra sak {{sakNummer}} {{sakTittel}} oppdatert', {
                    sakTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                    sakNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer)
                })) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se AgendaelementSkjerming#endringshendelsebygger i lederplan-tjenester */
        'endringshendelse-Sak-1-16': {
            kort: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Tilgangsbegrensninger for sak {{sakNummer}} {{sakTittel}} oppdatert', {
                    sakTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                    sakNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer)
                })) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.resources.sak.KopierAgendaelementFabrikk */
        'endringshendelse-Sak-0-30': {
            kort: (m: l.StrengMalEndringshendelse<SakKopiBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Sak {{tittel}} kopiert fra {{kildemoete_tittel}} {{kildemoete_nummer}}, {{moeteDato}}', {
                    tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                    kildemoete_tittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.kildemøte_tittel),
                    kildemoete_nummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.kildemøte_nummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.kildemøte_dato)
                })) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SakKopiBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se SakRessurs#slett i lederplan-tjenester */
        'endringshendelse-Sak-2': {
            kort: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Sak {{sakNummer}} {{sakTittel}} i {{moeteTittel}} {{moeteNummer}}, {{moeteDato}} er slettet', {
                    sakTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                    sakNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.nummer),
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.møteTittel),
                    moeteNummer: this.somSikker(m.noeklerOgVerdier.beskrivelse.møteNummer),
                    moeteDato: this.formaterMøteDato(m.noeklerOgVerdier.beskrivelse.møteDato)
                })) +
              this.endringshendelseSublinje(this.iMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SakBeskrivelsesArg>) => "" //bruk kort
        },


        /** Se sak.Avstemning#nyEndringsbeskrivelse i lederplan-tjenester  */
        'endringshendelse-Avstemning-0': {
            kort: (m: l.StrengMalEndringshendelse<AvstemningBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Ny avstemning {{tittel}} registrert', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<AvstemningBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se sak.Avstemning#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-Avstemning-1': {
            kort: (m: l.StrengMalEndringshendelse<AvstemningBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Avstemning {{tittel}} oppdatert', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<AvstemningBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se sak.Avstemning#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-Avstemning-2': {
            kort: (m: l.StrengMalEndringshendelse<AvstemningBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Avstemning {{tittel}} slettet', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<AvstemningBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se sak.Avstemning#åpneForAvstemning i lederplan-tjenester */
        'endringshendelse-Avstemning-1-9': {
            kort: (m: l.StrengMal<l.EndringshendelseBeskrivelse<AvstemningBeskrivelsesArg>>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Åpnet for stemmegivning på avstemning {{tittel}}', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: m => "" //bruk kort
        },

        /** Se sak.Avstemning#stengForAvstemning i lederplan-tjenester */
        'endringshendelse-Avstemning-1-10': {
            kort: (m: l.StrengMal<l.EndringshendelseBeskrivelse<AvstemningBeskrivelsesArg>>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Stengt for stemmegivning på avstemning {{tittel}}', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: m => "" //bruk kort
        },

        /** Se Beslutning#nyBeslutningshendelse i lederplan-tjenester  */
        'endringshendelse-Beslutning-0': {
            kort: (m: l.StrengMalEndringshendelse<BeslutningBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Ny beslutning {{beslutningTittel}} registrert', {beslutningTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<BeslutningBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Beslutning#nyBeslutningshendelse i lederplan-tjenester */
        'endringshendelse-Beslutning-1': {
            kort: (m: l.StrengMalEndringshendelse<BeslutningBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Beslutning {{beslutningTittel}} oppdatert', {beslutningTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<BeslutningBeskrivelsesArg>) => "" //bruk kort
        },

        'endringshendelse-Beslutning-2': {
            kort: (m: l.StrengMalEndringshendelse<BeslutningBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Beslutning {{beslutningTittel}} slettet', {beslutningTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<BeslutningBeskrivelsesArg>) => "" //bruk kort
        },


        /** Se Oppgave#oppgaveEndringshendelse i lederplan-tjenester */
        'endringshendelse-Oppgave-0': {
            kort: (m: l.StrengMalEndringshendelse<OppgaveBeskrivelsesArg>) => {
                let sub = '';
                if (!_.isNil(m.noeklerOgVerdier.beskrivelse.beslutning)) sub = this.endringshendelseSublinje(this.gettextCatalog.getString('under beslutning i sak {{sakTittel}} i {{moeteTittel}}, {{moeteDato}}', {
                    sakTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.sak),
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.moete),
                    moeteDato: this.formaterDato(m.noeklerOgVerdier.beskrivelse.moetedato)
                }));
                if (!_.isNil(m.noeklerOgVerdier.beskrivelse.sak)) sub = this.endringshendelseSublinje(this.gettextCatalog.getString('under sak {{sakTittel}} i {{moeteTittel}}, {{moeteDato}}', {
                    sakTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.sak),
                    moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.moete),
                    moeteDato: this.formaterDato(m.noeklerOgVerdier.beskrivelse.moetedato)
                }));
                return this.endringshendelseHovedlinje(this.gettextCatalog.getString('Ny oppgave {{oppgaveTittel}} registrert', {oppgaveTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
                  sub
            },
            full: (m: l.StrengMalEndringshendelse<OppgaveBeskrivelsesArg>) => "" //bruk kort
        },

        /**
         * Se Oppgave#oppgaveEndringshendelse i lederplan-tjenester */
        'endringshendelse-Oppgave-1': {
            kort: (m: l.StrengMalEndringshendelse<OppgaveBeskrivelsesArg>) => {
                let sub = '';
                //BeslutningsOppgaveBeskrivelse
                if (!_.isNil(m.noeklerOgVerdier.beskrivelse.beslutning)) sub = this.endringshendelseSublinje(this.gettextCatalog.getString(
                  'under beslutning i sak {{sakTittel}} i {{moeteTittel}}, {{moeteDato}}', {
                      sakTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.sak),
                      moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.moete),
                      moeteDato: this.formaterDato(m.noeklerOgVerdier.beskrivelse.moetedato)
                  }));
                //SaksOppgaveBeskrivelse
                if (!_.isNil(m.noeklerOgVerdier.beskrivelse.sak)) sub = this.endringshendelseSublinje(this.gettextCatalog.getString(
                  'under sak {{sakTittel}} i {{moeteTittel}}, {{moeteDato}}', {
                      sakTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.sak),
                      moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.moete),
                      moeteDato: this.formaterDato(m.noeklerOgVerdier.beskrivelse.moetedato)
                  }));
                return this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                    'Oppgave {{oppgaveTittel}} oppdatert', {oppgaveTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
                  sub
            },
            full: (m: l.StrengMalEndringshendelse<OppgaveBeskrivelsesArg>) => "" //bruk kort
        },

        'endringshendelse-Oppgave-2': {
            kort: (m: l.StrengMalEndringshendelse<OppgaveBeskrivelsesArg>) => {
                let sub = '';
                //BeslutningsOppgaveBeskrivelse
                if (!_.isNil(m.noeklerOgVerdier.beskrivelse.beslutning)) sub = this.endringshendelseSublinje(this.gettextCatalog.getString(
                  'under beslutning i sak {{sakTittel}} i {{moeteTittel}}, {{moeteDato}}', {
                      sakTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.sak),
                      moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.moete),
                      moeteDato: this.formaterDato(m.noeklerOgVerdier.beskrivelse.moetedato)
                  }));
                //SaksOppgaveBeskrivelse
                if (!_.isNil(m.noeklerOgVerdier.beskrivelse.sak)) sub = this.endringshendelseSublinje(this.gettextCatalog.getString(
                  'under sak {{sakTittel}} i {{moeteTittel}}, {{moeteDato}}', {
                      sakTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.sak),
                      moeteTittel: this.somSikker(m.noeklerOgVerdier.beskrivelse.moete),
                      moeteDato: this.formaterDato(m.noeklerOgVerdier.beskrivelse.moetedato)
                  }));
                return this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                    'Oppgave {{oppgaveTittel}} slettet', {oppgaveTittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
                  sub
            },
            full: (m: l.StrengMalEndringshendelse<OppgaveBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Medlem#nyEndringshendelse i lederplan-tjenester */
        'endringshendelse-Medlem-0': {
            kort: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => m.noeklerOgVerdier.beskrivelse.erBruker ?
              this.endringshendelseHovedlinje(this.gettextCatalog.getString('Nytt medlem {{navn}} registrert', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iGruppe(m)) :
              this.endringshendelseHovedlinje(this.gettextCatalog.getString('Nytt medlem {{navn}} invitert', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.tilGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Medlem#nyEndringshendelse i lederplan-tjenester */
        'endringshendelse-Medlem-1': {
            kort: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => {
                const verdier = {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)};
                if (m.noeklerOgVerdier.feltendringer.invitasjonsStatus && m.noeklerOgVerdier.feltendringer.invitasjonsStatus.nyVerdi === "akseptert")
                    return this.endringshendelseHovedlinje(this.gettextCatalog.getString('Medlem {{navn}} godtok invitasjonen', verdier)) +
                      this.endringshendelseSublinje(this.tilGruppe(m));

                //Medlem.harTilgang ble erstattet med Medlem.status i sak #1159
                if (m.noeklerOgVerdier.feltendringer.harTilgang && m.noeklerOgVerdier.feltendringer.harTilgang.nyVerdi === true)
                    return this.endringshendelseHovedlinje(this.gettextCatalog.getString('Medlem {{navn}} fikk tilgang', verdier)) +
                      this.endringshendelseSublinje(this.tilGruppe(m));

                if (m.noeklerOgVerdier.feltendringer.harTilgang && m.noeklerOgVerdier.feltendringer.harTilgang.nyVerdi === false)
                    return this.endringshendelseHovedlinje(this.gettextCatalog.getString('Medlem {{navn}} mistet tilgang', verdier)) +
                      this.endringshendelseSublinje(this.tilGruppe(m));

                if (m.noeklerOgVerdier.feltendringer.status) {
                    let hovedlinje = '';
                    switch (m.noeklerOgVerdier.feltendringer.status.nyVerdi) {
                        case l.Medlemstatus.normal_tilgang:
                            hovedlinje = this.gettextCatalog.getString('Medlem {{navn}} fikk tilgang', verdier);
                            break;
                        case l.Medlemstatus.tilgang_stengt:
                            hovedlinje = this.gettextCatalog.getString('Medlem {{navn}} mistet tilgang', verdier);
                            break;
                        case l.Medlemstatus.anonymisert:
                            hovedlinje = this.gettextCatalog.getString('Medlem {{navn}} har blitt permanent deaktivert og persondata fjernet', verdier);
                            break;
                    }
                    return this.endringshendelseHovedlinje(hovedlinje) + this.endringshendelseSublinje(this.tilGruppe(m));
                }

                return this.endringshendelseHovedlinje(this.gettextCatalog.getString('Medlem {{navn}} oppdatert', verdier)) +
                  this.endringshendelseSublinje(this.iGruppe(m));
            },
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },

        'endringshendelse-Medlem-1-25': {
            kort: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => {
                const verdier = {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)};
                return this.endringshendelseHovedlinje(this.gettextCatalog.getString('Medlem {{navn}} koblet med annen bruker', verdier)) +
                  this.endringshendelseSublinje(this.iGruppe(m));
            },
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },

        'endringshendelse-Medlem-1-27': {
            kort: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => {
                const verdier = {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)};
                return this.endringshendelseHovedlinje(this.gettextCatalog.getString('Medlem {{navn}} permanent deaktivert', verdier)) +
                  this.endringshendelseSublinje(this.iGruppe(m));
            },
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },


        /** Slett av Medlem/invitasjon */
        'endringshendelse-Medlem-2': {
            kort: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) =>
              this.endringshendelseHovedlinje(this.gettextCatalog.getString('Medlem {{navn}} slettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})) +
              this.endringshendelseSublinje(this.iGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se RollePeriode#nyEndringshendelse i lederplan-tjenester */
        'endringshendelse-RollePeriode-0': {
            kort: (m: l.StrengMalEndringshendelse<MedlemRollerPeriodeBeskrivelsesArg>) =>
              this.endringshendelseHovedlinje(this.gettextCatalog.getString('Rollen {{rolle}} tildelt medlem {{navn}}', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn), rolle: this.iTag(m.noeklerOgVerdier.beskrivelse.rolle)})) +
              this.endringshendelseSublinje(this.iGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se RollePeriode#nyEndringshendelse i lederplan-tjenester */
        'endringshendelse-RollePeriode-1': {
            kort: (m: l.StrengMalEndringshendelse<MedlemRollerPeriodeBeskrivelsesArg>) =>
              this.endringshendelseHovedlinje(this.gettextCatalog.getString('Rolleperiode endret for rollen {{rolle}} tildelt medlem {{navn}}', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn), rolle: this.iTag(m.noeklerOgVerdier.beskrivelse.rolle)})) +
              this.endringshendelseSublinje(this.iGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se RollePeriode#slett i lederplan-tjenester */
        'endringshendelse-RollePeriode-2': {
            kort: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) =>
              this.endringshendelseHovedlinje(this.gettextCatalog.getString('Rollen {{rolle}} slettet fra medlem {{navn}}', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn), rolle: this.iTag(m.noeklerOgVerdier.beskrivelse.rolle)})) +
              this.endringshendelseSublinje(this.iGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se MedlemsRolle#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-MedlemsRolle-0': {
            kort: (m: l.StrengMalEndringshendelse<MedlemsRolleBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Ny rolle {{tittel}} registrert', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemsRolleBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se MedlemsRolle#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-MedlemsRolle-1': {
            kort: (m: l.StrengMalEndringshendelse<MedlemsRolleBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Rolle {{tittel}} oppdatert', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemsRolleBeskrivelsesArg>) => "" //bruk kort
        },
        /** Se MedlemsRolle#nyEndringsbeskrivelse i lederplan-tjenester */
        'endringshendelse-MedlemsRolle-2': {
            kort: (m: l.StrengMalEndringshendelse<MedlemsRolleBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Rolle {{tittel}} slettet', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})) +
              this.endringshendelseSublinje(this.iGruppe(m)),
            full: (m: l.StrengMalEndringshendelse<MedlemsRolleBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Gruppe#nyGruppeEndringshendelse i lederplan-tjenester */
        'endringshendelse-Gruppe-0': {
            kort: (m: l.StrengMalEndringshendelse<GruppeBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Ny gruppe {{tittel}} ({{organisasjonsnavn}}) registrert', {
                  tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                  organisasjonsnavn: this.somSikker(m.noeklerOgVerdier.beskrivelse.organisasjonsnavn)
              })),
            full: (m: l.StrengMalEndringshendelse<GruppeBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Gruppe#nyGruppeEndringshendelse i lederplan-tjenester */
        'endringshendelse-Gruppe-0-29': {
            kort: (m: l.StrengMalEndringshendelse<GruppeBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Gruppe {{tittel}} ({{organisasjonsnavn}}) gjenopprettet fra sikkerhetskopi', {
                  tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                  organisasjonsnavn: this.somSikker(m.noeklerOgVerdier.beskrivelse.organisasjonsnavn)
              })),
            full: (m: l.StrengMalEndringshendelse<GruppeBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Gruppe#nyGruppeEndringshendelse i lederplan-tjenester */
        'endringshendelse-Gruppe-1': {
            kort: (m: l.StrengMalEndringshendelse<GruppeBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Gruppe {{tittel}} ({{organisasjonsnavn}}) oppdatert', {
                  tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel),
                  organisasjonsnavn: this.somSikker(m.noeklerOgVerdier.beskrivelse.organisasjonsnavn)
              })),
            full: (m: l.StrengMalEndringshendelse<GruppeBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Saksdokument#dokumentOpprettetEndringshendelse i lederplan-tjenester */
        'endringshendelse-Dokument-0': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Nytt dokument {{filnavn}} registrert', {
                    versjonsnummer: m.noeklerOgVerdier.beskrivelse.versjonsnummer,
                    filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)
                })) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: m => "" //bruk kort
        },

        /** Se OpplastetDokumentVersjon#nyDokumentVersjonEndringshendelse i lederplan-tjenester.
         NB: møte/saksinfo kun dersom eiende dokument er et saksdokument */
        'endringshendelse-Dokument-0-2': {
            kort: (m: l.StrengMalEndringshendelse<OpplastetDokumentVersjonBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Ny versjon ({{versjonsnummer}}) av dokumentet {{filnavn}} registrert', {
                    versjonsnummer: m.noeklerOgVerdier.beskrivelse.versjonsnummer,
                    filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)
                })) +
              (m.noeklerOgVerdier.beskrivelse.sakNummer ? this.endringshendelseSublinje(this.iSakOgMøte(m)) : ''),
            full: (m: l.StrengMalEndringshendelse<OpplastetDokumentVersjonBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.dokument.Signering#sendTilSignering i lederplan-tjenester.
         NB: møte/saksinfo kun dersom eiende dokument er et saksdokument */
        'endringshendelse-Dokument-1-4': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Dokumentet {{filnavn}} er klart til signering', {
                    filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)
                })) +
              (m.noeklerOgVerdier.beskrivelse.sakNummer ? this.endringshendelseSublinje(this.iSakOgMøte(m)) : ''),
            full: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.dokument.Signering#oppdaterSignatarer i lederplan-tjenester.
         NB: møte/saksinfo kun dersom eiende dokument er et saksdokument */
        'endringshendelse-Dokument-1-26': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Hvem som skal signere {{filnavn}} har blitt oppdatert', {
                    filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)
                })) +
              (m.noeklerOgVerdier.beskrivelse.sakNummer ? this.endringshendelseSublinje(this.iSakOgMøte(m)) : ''),
            full: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.dokument.Signering#registrerSignertSamledokument i lederplan-tjenester.
         NB: møte/saksinfo kun dersom eiende dokument er et saksdokument */
        'endringshendelse-Dokument-1-19': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Dokumentet {{filnavn}} er signert av alle signatarer og signaturbevis er generert', {
                    filnavn: this.somSikker(m.noeklerOgVerdier.beskrivelse.filnavn)
                })) +
              (m.noeklerOgVerdier.beskrivelse.sakNummer ? this.endringshendelseSublinje(this.iSakOgMøte(m)) : ''),
            full: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.dokument.Signering#låsOppDokument i lederplan-tjenester.
         NB: møte/saksinfo kun dersom eiende dokument er et saksdokument */
        'endringshendelse-Dokument-1-5': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Dokumentet {{filnavn}} er låst opp', {
                    filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)
                })) +
              (m.noeklerOgVerdier.beskrivelse.sakNummer ? this.endringshendelseSublinje(this.iSakOgMøte(m)) : ''),
            full: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se DokumentSkjerming#endringshendelsebygger i lederplan-tjenester.
         * Vhis dette er arkivdokument, så er ikke dette SaksdokumentBeskrivelsesArg, men DokumentVersjonBeskrivelsesArg.
         * */
        'endringshendelse-Dokument-1-16': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Tilgangsbegrensninger for dokumentet {{filnavn}} oppdatert', {filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)})) +
              (m.noeklerOgVerdier.beskrivelse.sakNummer ? this.endringshendelseSublinje(this.iSakOgMøte(m)) : ''),
            full: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.dokument.Signering#registrerEksterntSignertDokument i lederplan-tjenester. */
        'endringshendelse-Dokument-1-21': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Eksternt signert protokoll lastet opp, {{filnavn}}', {
                  filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)
              })),
            full: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => "" //bruk kort
        },

        /** Se Saksdokument#dokumentSlettetEndringshendelse i leerplan-tjenester */
        'endringshendelse-Dokument-2': {
            kort: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
                'Dokumentet {{filnavn}} slettet', {filnavn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn)})) +
              this.endringshendelseSublinje(this.iSakOgMøte(m)),
            full: (m: l.StrengMalEndringshendelse<SaksdokumentBeskrivelsesArg>) => "" //bruk kort
        },

        /** @TODO parametere i beskrivelse er forskjellig for mappe og dokument  */
        'endringshendelse-Arkivnode-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Nytt bibliotekinnslag {{navn}} opprettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn ? m.noeklerOgVerdier.beskrivelse.filnavn : m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** @TODO parametere i beskrivelse er forskjellig for mappe og dokument  */
        'endringshendelse-Arkivnode-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Bibliotekinnslag {{navn}} oppdatert', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn ? m.noeklerOgVerdier.beskrivelse.filnavn : m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** @TODO parametere i beskrivelse er forskjellig for mappe og dokument  */
        'endringshendelse-Arkivnode-2': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Bibliotekinnslag {{navn}} slettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn ? m.noeklerOgVerdier.beskrivelse.filnavn : m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** Tilgangsbegrensning for arkivnode. */
        'endringshendelse-Arkivnode-1-16': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Tilgangsbegrensninger for bibliotekinnslag {{navn}} oppdatert', {
                  navn: this.iTag(m.noeklerOgVerdier.beskrivelse.filnavn ? m.noeklerOgVerdier.beskrivelse.filnavn : m.noeklerOgVerdier.beskrivelse.navn)
              })),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Bruker-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Bruker {{navn}} oppdatert', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Bruker-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Bruker {{navn}} opprettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Bruker-6': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'E-post {{tittel}} sendt til {{navn}}', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel), navn: this.iTag(m.noeklerOgVerdier.beskrivelse.til)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Medlem-6': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'E-post {{tittel}} sendt til {{navn}}', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel), navn: this.iTag(m.noeklerOgVerdier.beskrivelse.til)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Sakstype-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Sakstype {{navn}} opprettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Sakstype-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Sakstype {{navn}} oppdatert', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Sakstype-2': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Sakstype {{navn}} slettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Årshjulinnslag-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Årshjulinnslag {{tittel}} opprettet', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Årshjulinnslag-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Årshjulinnslag {{tittel}} oppdatert', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Årshjulinnslag-2': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Årshjulinnslag {{tittel}} slettet', {tittel: this.iTag(m.noeklerOgVerdier.beskrivelse.tittel)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Tema-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Tema {{navn}} opprettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Tema-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Tema {{navn}} oppdatert', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Tema-2': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Tema {{navn}} slettet', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Plan-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Plan {{periode}} redigert', {periode: this.iTag(this.planPeriodeSomStreng(m.noeklerOgVerdier.beskrivelse.periode))})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Aksjebeholdning-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Beholdning oppdatert', {})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Aksjebeholdning-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Beholdning for {{navn}} oppdatert', {navn: this.iTag(m.noeklerOgVerdier.beskrivelse.navn)})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Aksjebeholdning-2': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Beholdning for {{navn}} oppdatert', {navn: m.noeklerOgVerdier.beskrivelse.navn})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Aksjeeier-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Aksjonær {{navn}} opprettet', {navn: m.noeklerOgVerdier.beskrivelse.navn})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Aksjeeier-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Aksjonær {{navn}} endret', {navn: m.noeklerOgVerdier.beskrivelse.navn})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        'endringshendelse-Aksjeklasse-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Aksjeklasse {{navn}} opprettet', {navn: m.noeklerOgVerdier.beskrivelse.navn})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Aksjeklasse-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Aksjeklasse {{navn}} endret', {navn: m.noeklerOgVerdier.beskrivelse.navn})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
        'endringshendelse-Aksjeklasse-2': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Aksjeklasse {{navn}} slettet', {navn: m.noeklerOgVerdier.beskrivelse.navn})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** no.systemfabrikken.lederplan.domene.spørreundersøkelse.Spørreundersøkelse.SpørreundersøkelseBygger.byggMedHendelse */
        'endringshendelse-Spørreundersøkelse-0': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Spørreundersøkelse "{{tittel}}" opprettet', {tittel: m.noeklerOgVerdier.beskrivelse.tittel})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.spørreundersøkelse.Spørreundersøkelse.oppdater */
        'endringshendelse-Spørreundersøkelse-1': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Spørreundersøkelse "{{tittel}}" endret', {tittel: m.noeklerOgVerdier.beskrivelse.tittel})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.spørreundersøkelse.Spørreundersøkelse.åpneForBesvarelse */
        'endringshendelse-Spørreundersøkelse-1-31': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Spørreundersøkelse "{{tittel}}" åpnet for besvarelse', {tittel: m.noeklerOgVerdier.beskrivelse.tittel})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.spørreundersøkelse.Spørreundersøkelse.stengForBesvarelse */
        'endringshendelse-Spørreundersøkelse-1-32': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Spørreundersøkelse "{{tittel}}" stengt for besvarelse', {tittel: m.noeklerOgVerdier.beskrivelse.tittel})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },

        /** Se no.systemfabrikken.lederplan.domene.spørreundersøkelse.Spørreundersøkelse.oppdater */
        'endringshendelse-Spørreundersøkelse-2': {
            kort: (m: l.StrengMalEndringshendelse<any>) => this.endringshendelseHovedlinje(this.gettextCatalog.getString(
              'Spørreundersøkelse "{{tittel}}" slettet', {tittel: m.noeklerOgVerdier.beskrivelse.tittel})),
            full: (m: l.StrengMalEndringshendelse<any>) => "" //bruk kort
        },
    };

    private planPeriodeSomStreng(periode: l.PlanPeriode): string {
        if (periode === null) return '';
        return moment(periode.start).format("L") + ' - ' + moment(periode.slutt).format("L");
    }

}

//interface som definerer innhold i foreldrebeskrivelse (se statiske metoder i StrengMal.java)

interface MøteBeskrivelse {
    moete: string;
    moetedato: Date;
}

interface SaksBeskrivelse {
    sak: string;
    moete: string;
    moetedato: Date;
}

interface BeslutningBeskrivelse extends SaksBeskrivelse {
    beslutning: string;
}

/** arver fra BeslutningsOppgaveBeskrivelse dersom oppgave er knyttet til Beslutning,
 * arver fra SaksOppgaveBeskrivelse dersom oppgave er knyttet til sak*/
interface OppgaveBeskrivelse extends BeslutningsOppgaveBeskrivelse, SaksOppgaveBeskrivelse {
    oppgave: string;
}

interface SaksKommentarBeskrivelse {
    sak: string;
    moete: string;
    moetedato: Date;
}

/** arver fra BeslutningsOppgaveBeskrivelse dersom oppgave er knyttet til Beslutning,
 * arver fra SaksOppgaveBeskrivelse dersom oppgave er knyttet til sak*/
interface OppgaveKommentarBeskrivelse extends BeslutningsOppgaveBeskrivelse, SaksOppgaveBeskrivelse {
    oppgave: string;
}

interface DokumentAnnoteringKommentarBeskrivelse {
    dokument: string;

    //dersom annotering er knyttet til et saksdokument
    sak: string;
    moete: string;
    moetedato: Date;

    //dersom annotering er knyttet til et arkivdokument
    mappenavn: string;
}

interface BeslutningsOppgaveBeskrivelse {
    beslutning: string;
    sak: string;
    moete: string;
    moetedato: Date;
}

interface SaksOppgaveBeskrivelse {
    sak: string;
    moete: string;
    moetedato: Date;
}

//interface som definerer innhold i endringshendelser beskrivelse-felt (se BeskrivelsesargumenterFabrikk.java)

interface Sp2MigrertBeskrivelsesArg {
    responsible: string;
    message: string;
    detailed: string;
    masked: string;
}

interface AvtaleBeskrivelsesArg {
    navn: string;
}

interface MøteBeskrivelsesArg {
    tittel: string;
    nummer: string;
    dato: l.MøteDato;
    formål: string;
    behandlingsMåte: string;
    behandlingsSted: string;
    gruppenavn: string;
    organisasjonsnavn: string;
}

interface MøteKopiBeskrivelsesArg extends MøteBeskrivelsesArg {
    kildemøte_tittel: string;
    kildemøte_nummer: string;
    kildemøte_dato: l.MøteDato;
    kildemøte_gruppenavn: string;
    kildemøte_organisasjonsnavn: string;
}

interface SakKopiBeskrivelsesArg extends Møte {
    tittel: string;
    nummer: string;

    kildemøte_tittel: string;
    kildemøte_nummer: string;
    kildemøte_dato: l.MøteDato;
    kildemøte_gruppenavn: string;
    kildemøte_organisasjonsnavn: string;
}

interface MøtedeltakerBeskrivelsesArg extends Møte, Møtedeltaker {
    tittel: string;
}

interface ProtokollførtMøtedeltakerBeskrivelsesArg extends Møte {
    navn: string;
    epost: l.MøteDato;
    status: l.ProtokollførtDeltakerstatus; //til forskjell fra Møtedeltaker(..)
}

interface SignatarBeskrivelsesArg extends DokumentVersjonBeskrivelsesArg {
    navn: string;
    epost: l.MøteDato;
}

// arver kun fra en av SaksdokumentBeskrivelsesArg,  ProtokolldokumentBeskrivelsesArg eller ArkivFilnodeBeskrivelsesArg
interface SignertHendelseArg extends SaksdokumentBeskrivelsesArg, ProtokolldokumentBeskrivelsesArg, ArkivFilnodeBeskrivelsesArg {
    navn: string;
    signeringsmetode: l.BenyttetSigneringsmetode;
}

interface MøtedeltakersStemmegivningBeskrivelsesArg extends MøtedeltakerBeskrivelsesArg, Sak {
    avstemningTittel: string;
}

interface SakBeskrivelsesArg extends Møte {
    tittel: string;
    nummer: string;
}

interface AvstemningBeskrivelsesArg extends MøteOgSak {
    tittel: string;
}

interface BeslutningBeskrivelsesArg extends MøteOgSak {
    tittel: string;
}

/** arver fra BeslutningsOppgaveBeskrivelse dersom oppgave er knyttet til Beslutning,
 * arver fra SaksOppgaveBeskrivelse dersom oppgave er knyttet til sak*/
interface OppgaveBeskrivelsesArg extends BeslutningsOppgaveBeskrivelse, SaksOppgaveBeskrivelse {
    tittel: string;
    frist: Date | null;
    ansvarlig: string
}

interface MedlemBeskrivelsesArg extends Gruppe {
    navn: string;
    harTilgang: boolean;
    rolle: string;
    epost: string;
    mobilnummer: string;
    erBruker: string;
    invitasjonsStatus: string;//@todo burde vært tallrepresentasjon
}

interface MedlemRollerPeriodeBeskrivelsesArg extends Gruppe {
    navn: string;
    rolle: string;
    periode: string;
}

interface MedlemsRolleBeskrivelsesArg extends Gruppe {
    tittel: string;
}

interface GruppeBeskrivelsesArg {
    tittel: string;
    organisasjonsnavn: string;
    møtenavn: string;
}

interface SaksdokumentBeskrivelsesArg extends DokumentVersjonBeskrivelsesArg, MøteOgSak {
}

interface ProtokolldokumentBeskrivelsesArg extends DokumentVersjonBeskrivelsesArg, Møte {
}

interface ArkivFilnodeBeskrivelsesArg extends DokumentVersjonBeskrivelsesArg {
    mappenavn: string;
}

/** arver fra MøteOgSak dersom eiende dokument er saksdokument */
interface OpplastetDokumentVersjonBeskrivelsesArg extends DokumentVersjonBeskrivelsesArg, MøteOgSak {
}

interface DokumentVersjonBeskrivelsesArg {
    filnavn: string;
    filstørrelse: number | null;
    antallSider: number | null;
    versjonsnummer: number;
}

//@todo håndter denne endringshendelsen
interface SpørreundersøkelseBeskrivelsesArg {
    tittel: string;
    gruppenavn: string;
    organisasjonsnavn: string;
}

interface Gruppe {
    gruppe: string;
}

interface MøteOgSak extends Møte, Sak {
}

interface Møte {
    møteNummer: string;
    møteDato: l.MøteDato;
    møteTittel: string;
}

interface Møtedeltaker {
    navn: string;
    epost: l.MøteDato;
    status: string; //@todo burde vært tallRepresentasjon, ref protokollførtMøtedeltakerBeskrivelsesArg
}

interface Sak {
    sakTittel: string;
    sakNummer: string;
}
